import React, { useEffect, useState } from "react";

import { Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import { Loader } from "@components/ClinicalNewReportDialog/Loader";

/**
 * Verbatim attestation copy — exported so the same text we display can be
 * logged in the audit event. Keeping it as a constant means the displayed
 * wording and the recorded wording can never drift.
 */
const APPROVE_ALL_ATTESTATION_TEXT =
  "I confirm that I have reviewed all content in this report and attest that it reflects my independent clinical judgment. I understand that AI-generated summaries are my responsibility once approved.";

export interface ApproveAllAttestation {
  /** The verbatim text the clinician acknowledged. */
  text: string;
  /** ISO-8601 timestamp at the moment the clinician clicked Approve all. */
  attestedAt: string;
}

interface ApproveAllAttestationDialogProps {
  open: boolean;
  isApplying: boolean;
  progressContent?: React.ReactNode;
  onCancel: () => void;
  onConfirm: (attestation: ApproveAllAttestation) => void;
}

export const ApproveAllAttestationDialog: React.FC<ApproveAllAttestationDialogProps> = ({
  open,
  isApplying,
  progressContent,
  onCancel,
  onConfirm,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset the checkbox each time the dialog opens — a previous session's
  // tick must not carry over into a new attestation moment.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const submit = () => {
    if (!acknowledged || isApplying) return;
    onConfirm({
      text: APPROVE_ALL_ATTESTATION_TEXT,
      attestedAt: new Date().toISOString(),
    });
  };

  return (
    <Dialog
      open={open}
      setOpen={(next) => {
        if (!next && !isApplying) onCancel();
      }}
      title="Approve all goals and targets"
      size="xl"
      showCloseButton={!isApplying}
      primaryButton={{
        text: (
          <span className="flex items-center justify-center gap-2">
            {isApplying ? <Loader className="ml-0 mr-0 h-4 w-4 text-white" /> : null}
            Approve all
          </span>
        ),
        disabled: !acknowledged || isApplying,
        onClick: submit,
        type: "submit",
        buttonAction: "regular",
      }}
      secondaryButton={{
        text: "Cancel",
        disabled: isApplying,
        onClick: onCancel,
      }}
    >
      <div className="flex flex-col gap-4 py-2 text-text-primary">
        <Paragraph size="medium" colorType="primary">
          Approving all goals and targets confirms that you have personally reviewed each goal, target, and AI-generated
          summary in this report for clinical accuracy, appropriateness, and completeness.
        </Paragraph>
        <Paragraph size="medium" colorType="primary" displayType="loud">
          This action cannot be undone.
        </Paragraph>
        <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-md border border-border-strong bg-limestone-50 p-3">
          <input
            type="checkbox"
            checked={acknowledged}
            disabled={isApplying}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border-strong text-interactive-primary-default focus:ring-interactive-primary-default"
            aria-label="Attestation acknowledgement"
          />
          <span className="text-sm text-text-primary">{APPROVE_ALL_ATTESTATION_TEXT}</span>
        </label>
        {isApplying && progressContent}
      </div>
    </Dialog>
  );
};
